import { theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import { ImageSize } from "~/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import { ArticleContentRightSection } from "~/components/article/article-render/article-content/article-content-right-section";
import { useReduceWhitespaceByAvailableSpace } from "~/components/article/article-render/hooks";

import {
  Author,
  AuthorDetail,
  Comment,
  Content,
  DesktopHeaderAdSlot,
  Follow,
  GenericArticleContainer,
  Headline,
  Leading,
  Left,
  MobileContentFooterAdSlot,
  MobileContentHeaderAdSlot,
  MobileFooterAdSlot,
  MoreOnThis,
  Section,
  Sponsor,
  SubHeadline,
  TabletContentFooterAdSlot,
  Topic,
  TopPicksAdSlot,
  WidgetsBottom,
  WidgetsTop,
} from "./styles";

export const GenericArticle: FunctionComponent<ArticleRenderChildProps> = ({
  isShowInlinePaywall,
  provider,
}) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace(
    {
      adSlotSize: 600,
      nativeAdSlotSize: 260,
      whitespaceAllowance: 100,
    },
    isShowInlinePaywall,
  );

  return (
    <GenericArticleContainer>
      <WidgetsTop>{provider.topicFollowWidget({ variant: "stack" })}</WidgetsTop>
      <WidgetsBottom>
        {provider.leftHandSideWidgets({
          topPicksWidgetProps: {
            maximumHeight: reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.maximumHeight,
            slots: {
              footer: {
                estimatedHeight: 260,
                node: (
                  <TopPicksAdSlot>
                    {provider.adslot({
                      adUnit: "d_native3b",
                      breakpoint: theme.breakpoints.up("desktop"),
                      sizes: ["fluid"],
                    })}
                  </TopPicksAdSlot>
                ),
              },
              header: {
                estimatedHeight: 260,
                node: (
                  <TopPicksAdSlot>
                    {provider.adslot({
                      adUnit: "d_native2b",
                      breakpoint: theme.breakpoints.up("desktop"),
                      fluidDefaultSize: {
                        height: 260,
                      },
                      sizes: ["fluid"],
                    })}
                  </TopPicksAdSlot>
                ),
              },
            },
          },
        })}
      </WidgetsBottom>
      {reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.trackerNode}

      <Leading>
        {provider.leading({
          responsiveDescriptionVariants: {
            desktopUp: "show",
            mobileUp: "hidden",
            tabletUp: "show",
          },
          responsiveImageVariants: { desktopUp: "normal", mobileUp: "square", tabletUp: "normal" },
          responsiveVariants: {
            desktopUp: ImageSize["1200x800"],
            mobileUp: ImageSize["768x768"],
            tabletUp: ImageSize["768x768"],
          },
        })}
      </Leading>
      <Section>{provider.sections()}</Section>
      <Headline>{provider.headline()}</Headline>
      <SubHeadline>{provider.subHeadline()}</SubHeadline>
      <Sponsor>{provider.sponsor()}</Sponsor>
      <Follow>{provider.topicFollowWidget({ variant: "banner" })}</Follow>
      <Author>{provider.authorBlock()}</Author>
      <Content>
        {provider.body({
          inlineAdSlots: {
            "0": {
              adUnit: "outstream1",
            },
            "1": {
              adUnit: "m_lrec1",
            },
            "2": {
              adUnit: "d_lrec2",
            },
            "3": {
              adUnit: "m_lrec4d",
            },
            "4": {
              adUnit: "m_lrec4e",
            },
            "5": {
              adUnit: "m_lrec4f",
            },
            "6": {
              adUnit: "native5",
            },
          },
        })}
      </Content>
      <Comment>{provider.commentTrigger()}</Comment>
      <MoreOnThis>{provider.moreOnThis({ variant: "horizontal" })}</MoreOnThis>
      <AuthorDetail>{provider.authorDetails()}</AuthorDetail>
      <Topic>{provider.topics()}</Topic>
      <Left $stickyStackedHeight={appBarStickyHeight}>
        {provider.shareWidget()}
        {provider.actionWidget()}
      </Left>
      <ArticleContentRightSection
        {...reduceWhitespaceByAvailableSpaceSpec.right}
        provider={provider}
      />
      {reduceWhitespaceByAvailableSpaceSpec.right.trackerNode}

      <DesktopHeaderAdSlot>
        {provider.adslot({
          adUnit: "d_banner1",
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
        })}
      </DesktopHeaderAdSlot>

      <TabletContentFooterAdSlot>
        {provider.adslot({
          adUnit: "native4",
          autoRefreshOptions: {
            disableIntervalRefresh: true,
            disableScrollBackRefresh: true,
          },
          breakpoint: theme.breakpoints.only("tablet"),
          sizes: ["fluid"],
        })}
      </TabletContentFooterAdSlot>

      <MobileContentHeaderAdSlot>
        {provider.adslot({
          adUnit: "m_banner1",
          breakpoint: theme.breakpoints.only("mobile"),
          sizes: [[300, 250], "fluid"],
          withBackground: true,
          withLabel: true,
        })}
      </MobileContentHeaderAdSlot>
      <MobileContentFooterAdSlot>
        {provider.adslot({
          adUnit: "native4",
          autoRefreshOptions: {
            disableIntervalRefresh: true,
            disableScrollBackRefresh: true,
          },
          breakpoint: theme.breakpoints.only("mobile"),
          sizes: ["fluid"],
        })}
      </MobileContentFooterAdSlot>
      <MobileFooterAdSlot>
        {provider.adslot({
          adUnit: "m_lrec3a",
          breakpoint: theme.breakpoints.only("mobile"),
          sizes: [[300, 250], "fluid"],
          withBackground: true,
          withLabel: true,
        })}
      </MobileFooterAdSlot>
    </GenericArticleContainer>
  );
};

GenericArticle.displayName = "GenericArticle";
